"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronDown, ArrowUpRight } from "lucide-react";

const FORMAT_LABELS: Record<string, string> = {
  ebook: "eBook",
  paperback: "Paperback",
  hardcover: "Hardcover",
  audiobook: "Audiobook",
};

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  es: "Spanish",
};

interface PurchaseDropdownProps {
  purchaseLinks: { format: string; url: string; language?: string }[];
}

function linkLabel(link: { format: string; language?: string }): string {
  const formatLabel = FORMAT_LABELS[link.format] || link.format;
  if (link.language && LANGUAGE_LABELS[link.language]) {
    return `${formatLabel} (${LANGUAGE_LABELS[link.language]})`;
  }
  return formatLabel;
}

export function PurchaseDropdown({ purchaseLinks }: PurchaseDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!purchaseLinks || purchaseLinks.length === 0) return null;

  if (purchaseLinks.length === 1) {
    return (
      <a
        href={purchaseLinks[0].url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-charcoal px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-charcoal-light group"
      >
        Amazon
        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    );
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 bg-charcoal px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-charcoal-light group"
      >
        Purchase on Amazon
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full z-10 mt-1 min-w-[180px] rounded border border-border bg-white py-1 shadow-lg">
          {purchaseLinks.map((link, i) => (
            <a
              key={`${link.format}-${link.language ?? "en"}-${i}`}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-sm text-charcoal hover:bg-ivory"
            >
              {linkLabel(link)}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
